import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	Trash2,
	CheckCircle,
	Archive,
	AlertCircle,
	CheckCircle2,
	Info,
	AlertTriangle,
	Calendar,
	User,
	Link as LinkIcon,
} from "lucide-react";
import { notificationsAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Loading } from "@/components/ui/loader";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { formatDateTime } from "@/lib/utils";

interface BackendNotification {
	id: string;
	title: string;
	message: string;
	type: "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "SYSTEM" | "BUSINESS";
	status: "UNREAD" | "READ" | "ARCHIVED";
	priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
	createdAt: string;
	readAt?: string;
	expiresAt?: string;
	actionUrl?: string;
	actionText?: string;
	createdBy?: {
		id: string;
		firstName?: string;
		lastName?: string;
		email?: string;
	};
	metadata?: any;
}

const NotificationDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { isRTL } = useLanguage();

	const [notification, setNotification] = useState<BackendNotification | null>(null);
	const [loading, setLoading] = useState(true);
	const [processing, setProcessing] = useState(false);

	useEffect(() => {
		if (id) {
			fetchNotification();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchNotification = async () => {
		try {
			setLoading(true);
			const data = await notificationsAPI.getById(id!);
			setNotification(data);

			// Auto-mark as read if unread
			if (data.status === "UNREAD") {
				await notificationsAPI.markAsRead(id!);
				setNotification(prev => (prev ? { ...prev, status: "READ", readAt: new Date().toISOString() } : null));
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("notifications.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/notifications");
		} finally {
			setLoading(false);
		}
	};

	const handleMarkAsRead = async () => {
		if (!notification || notification.status === "READ") return;

		try {
			setProcessing(true);
			await notificationsAPI.markAsRead(notification.id);
			setNotification(prev => (prev ? { ...prev, status: "READ", readAt: new Date().toISOString() } : null));
			toast({
				title: t("notifications.messages.updateSuccess"),
				description: t("notifications.messages.markedAsRead"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("notifications.messages.updateError"),
				description: errorMessage || t("notifications.errors.markAsReadFailed"),
				variant: "destructive",
			});
		} finally {
			setProcessing(false);
		}
	};

	const handleArchive = async () => {
		if (!notification) return;

		try {
			setProcessing(true);
			await notificationsAPI.markAsArchived(notification.id);
			setNotification(prev => (prev ? { ...prev, status: "ARCHIVED" } : null));
			toast({
				title: t("notifications.messages.updateSuccess"),
				description: t("notifications.messages.archived"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("notifications.messages.updateError"),
				description: errorMessage || t("notifications.errors.archiveFailed"),
				variant: "destructive",
			});
		} finally {
			setProcessing(false);
		}
	};

	const handleDelete = async () => {
		if (!notification) return;

		if (await confirmAction(t("notifications.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				setProcessing(true);
				await notificationsAPI.delete(notification.id);
				toast({
					title: t("notifications.messages.deleteSuccess"),
					description: t("notifications.messages.deleteSuccessDescription"),
				});
				navigate("/notifications");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("notifications.messages.deleteError"),
					description: errorMessage || t("notifications.errors.deleteFailed"),
					variant: "destructive",
				});
			} finally {
				setProcessing(false);
			}
		}
	};

	const getTypeIcon = (type: string) => {
		const iconConfig = {
			INFO: <Info className="h-5 w-5 text-primary" />,
			SUCCESS: <CheckCircle2 className="h-5 w-5 text-success" />,
			WARNING: <AlertTriangle className="h-5 w-5 text-warning" />,
			ERROR: <AlertCircle className="h-5 w-5 text-destructive" />,
			SYSTEM: <Info className="h-5 w-5 text-muted-foreground" />,
			BUSINESS: <Info className="h-5 w-5 text-primary" />,
		};
		return iconConfig[type as keyof typeof iconConfig] || <Info className="h-5 w-5" />;
	};

	const getTypeBadge = (type: string) => {
		const typeConfig = {
			INFO: { variant: "bg-primary/10 text-primary", text: t("notifications.type.info") },
			SUCCESS: { variant: "bg-success/10 text-success", text: t("notifications.type.success") },
			WARNING: { variant: "bg-warning/10 text-warning", text: t("notifications.type.warning") },
			ERROR: { variant: "bg-destructive/10 text-destructive", text: t("notifications.type.error") },
			SYSTEM: { variant: "bg-muted text-foreground", text: t("notifications.type.system") },
			BUSINESS: { variant: "bg-primary/10 text-primary", text: t("notifications.type.business") },
		};
		const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.INFO;
		return <Badge className={config.variant}>{config.text}</Badge>;
	};

	const getPriorityBadge = (priority: string) => {
		const priorityConfig = {
			LOW: { variant: "bg-muted text-foreground", text: t("notifications.priority.low") },
			MEDIUM: { variant: "bg-warning/10 text-warning", text: t("notifications.priority.medium") },
			HIGH: { variant: "bg-warning/10 text-warning", text: t("notifications.priority.high") },
			URGENT: { variant: "bg-destructive/10 text-destructive", text: t("notifications.priority.urgent") },
		};
		const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;
		return <Badge className={config.variant}>{config.text}</Badge>;
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			UNREAD: { variant: "bg-warning/10 text-warning", text: t("notifications.status.unread") },
			READ: { variant: "bg-success/10 text-success", text: t("notifications.status.read") },
			ARCHIVED: { variant: "bg-muted text-foreground", text: t("notifications.status.archived") },
		};
		const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.UNREAD;
		return <Badge className={config.variant}>{config.text}</Badge>;
	};

	if (loading) return <Loading />;

	if (!notification) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("notifications.errors.fetchFailed")}</p>
				<Button onClick={() => navigate("/notifications")} className="mt-4">
					{t("common.back")}
				</Button>
			</div>
		);
	}

	return (
		<div className={`space-y-6 p-6 ${isRTL ? "text-right" : "text-left"}`}>
			{/* Header */}
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("notifications.actions.view")}</PageHeading>
						<p className="text-muted-foreground">{notification.title}</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{notification.status === "UNREAD" && (
						<Button variant="outline" onClick={handleMarkAsRead} disabled={processing}>
							<CheckCircle className="h-4 w-4 mx-2" />
							{t("notifications.markAsRead")}
						</Button>
					)}
					{notification.status !== "ARCHIVED" && (
						<Button variant="outline" onClick={handleArchive} disabled={processing}>
							<Archive className="h-4 w-4 mx-2" />
							{t("notifications.actions.archive")}
						</Button>
					)}
					<Button variant="destructive" onClick={handleDelete} disabled={processing}>
						<Trash2 className="h-4 w-4 mx-2" />
						{t("common.delete")}
					</Button>
				</div>
			</div>

			{/* Basic Information */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						{getTypeIcon(notification.type)}
						{t("notifications.fields.title")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div>
						<h3 className="text-lg font-semibold mb-2">{notification.title}</h3>
						{notification.message && (
							<p className="text-muted-foreground whitespace-pre-wrap">{notification.message}</p>
						)}
					</div>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
						<div>
							<p className="text-sm text-muted-foreground mb-1">{t("notifications.fields.type")}</p>
							{getTypeBadge(notification.type)}
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">{t("notifications.fields.priority")}</p>
							{getPriorityBadge(notification.priority)}
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">{t("notifications.fields.status")}</p>
							{getStatusBadge(notification.status)}
						</div>
						{notification.actionUrl && (
							<div>
								<p className="text-sm text-muted-foreground mb-1">
									{t("notifications.fields.actionUrl")}
								</p>
								<a
									href={notification.actionUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary hover:underline flex items-center gap-1"
								>
									<LinkIcon className="h-4 w-4" />
									{notification.actionText || t("notifications.fields.actionUrl")}
								</a>
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Details */}
			<Card>
				<CardHeader>
					<CardTitle>{t("notifications.table.details")}</CardTitle>
					<CardDescription>{t("notifications.detail.description")}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="flex items-start gap-3">
							<Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
							<div>
								<p className="text-sm text-muted-foreground">{t("notifications.fields.createdAt")}</p>
								<p className="font-medium">{formatDateTime(notification.createdAt)}</p>
							</div>
						</div>

						{notification.readAt && (
							<div className="flex items-start gap-3">
								<CheckCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
								<div>
									<p className="text-sm text-muted-foreground">{t("notifications.fields.readAt")}</p>
									<p className="font-medium">{formatDateTime(notification.readAt)}</p>
								</div>
							</div>
						)}

						{notification.expiresAt && (
							<div className="flex items-start gap-3">
								<Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
								<div>
									<p className="text-sm text-muted-foreground">
										{t("notifications.fields.expiresAt")}
									</p>
									<p className="font-medium">{formatDateTime(notification.expiresAt)}</p>
								</div>
							</div>
						)}

						{notification.createdBy && (
							<div className="flex items-start gap-3">
								<User className="h-5 w-5 text-muted-foreground mt-0.5" />
								<div>
									<p className="text-sm text-muted-foreground">
										{t("notifications.detail.createdBy")}
									</p>
									<p className="font-medium">
										{notification.createdBy.firstName} {notification.createdBy.lastName}
										{notification.createdBy.email && (
											<span className="text-muted-foreground text-sm ms-2">
												({notification.createdBy.email})
											</span>
										)}
									</p>
								</div>
							</div>
						)}
					</div>

					{notification.metadata && Object.keys(notification.metadata).length > 0 && (
						<div className="pt-4 border-t">
							<p className="text-sm text-muted-foreground mb-2">{t("notifications.detail.metadata")}</p>
							<pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
								{JSON.stringify(notification.metadata, null, 2)}
							</pre>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default NotificationDetailPage;
